import { AxiosResponse } from "axios";
import { Dispatch } from "@reduxjs/toolkit";

import {
  setHasCartItem,
  setCartItemEmpty,
  setCartItemListing,
  setIsProcessingCheckout,
} from "./slice";
import {
  showAlert,
  isAPISuccessful,
  checkInternetAndShowAlert,
} from "../../../common/utils";
import { setLoading } from "../../../redux/global.slice";
import { postApiCall, putApiCall } from "../../../common/constants";
import { Module, EndPoint, getApiCall } from "../../../common/constants";
import { apiSuccessCode } from "../../../common/constants/axios.instance";
import {
  AnalyticsEvents,
  capturePosthogEvent,
} from "../../../common/utils/analytics";
import { RootState } from "../../../redux/store";

export const onAddToCart =
  (values: any, callback?: () => void) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.addToCart,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          showAlert(1, data.message);
          if (callback) callback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const getCartHasItems =
  (callback?: () => void) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    getApiCall(
      Module.manufacturer + EndPoint.checkCart,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          const hasItem = data?.data?.hasItem;
          dispatch(setHasCartItem(hasItem));
          if (callback) callback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const getCartListing =
  (callback?: () => void) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    getApiCall(
      Module.manufacturer + EndPoint.cartListing,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          dispatch(setCartItemListing(data?.data));

          if (callback) callback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const onCartCheckout =
  (values: any, callback?: (cartTicketId: string) => void) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.cartCheckout,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          showAlert(1, data.message);
          const cartTicketId = data?.data?.cartTicketId;
          if (callback) callback(cartTicketId);
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const onCartCheckoutSuccess =
  (values: any, callback?: () => void) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.cartCheckoutSuccess,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          showAlert(1, data.message);
          if (callback) callback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const removeCartItem =
  (cartItemId: string, callback?: () => void) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    putApiCall(
      `${Module.manufacturer}${EndPoint.removeCartItem}/${cartItemId}`,
      {},
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          dispatch(getCartListing());
          if (callback) callback();
        } else if (data.error) showAlert(2, data.message);
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
      },
    );
  };

export const getCartActiveCheckout =
  (callback?: () => void) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    getApiCall(
      Module.manufacturer + EndPoint.checkActiveCheckout,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          const isProcessingCheckout = data?.data?.isProcessingCheckout;
          dispatch(setIsProcessingCheckout(isProcessingCheckout));
          if (callback) callback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const updateCartQuantity =
  (
    cartItemId: string,
    rollQuantity: number,
    callback?: () => void,
    errorCallback?: () => void,
  ) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));

    putApiCall(
      `${Module.manufacturer}${EndPoint.cartListing}/${cartItemId}?rollQuantity=${rollQuantity}`,
      {},
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          callback && callback();
        } else if (data.error) {
          showAlert(2, data.message);
          errorCallback && errorCallback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));

        showAlert(2, error?.message);
        errorCallback && errorCallback();
      },
    );
  };

export const accountCheckoutPurchase =
  (values: any, callback?: () => void) =>
  (dispatch: Dispatch<any>, getState: () => RootState) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));
    const userId = getState().verifyEmail.userId;

    postApiCall(
      Module.manufacturer + EndPoint.buyNow,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          capturePosthogEvent(AnalyticsEvents.CART_CHECKOUT_SUCCESS, {
            user_id: userId,
            cart_items: values.cartItems,
          });
          if (callback) callback();
          showAlert(1, data.message);
          dispatch(setCartItemEmpty());
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };
